package fyi.moko.hotpl.domain.usecase

import fyi.moko.hotpl.data.image.PlaceImageStore
import fyi.moko.hotpl.domain.model.PhotoGroup
import fyi.moko.hotpl.domain.repository.PlaceRepository

/**
 * Fixes a photo that landed in the wrong bucket after a visit was already saved — the
 * picker-time correction ([fyi.moko.hotpl.ui.detail.DetailViewModel.onVisitPhotoGroupChange])
 * only works before saving, so this is the same idea for a photo that already made it into
 * a place's gallery or a visit.
 *
 * ── 왜 가게 → 방문 방향은 없는가 ──────────────────────────────
 * [PhotoRef]는 어느 방문에서 찍힌 사진인지 기억하지 않는다. 가게 사진은 시간이 지나도
 * 그 가게의 모습이라는 전제라 원래 그렇다 — 저장되는 순간 방문과의 연결이 끊긴다. 그래서
 * 가게 갤러리의 사진은 [removeFromPlace]로 지우는 것만 가능하고, 특정 방문으로 돌려보낼
 * 수는 없다. 반대로 방문 사진은 아직 그 방문에 붙어 있으므로 [promoteToPlace]로 옮길 수
 * 있다.
 */
class CorrectPhotoUseCase(
    private val repository: PlaceRepository,
    private val imageStore: PlaceImageStore,
) {
    /** Moves a visit photo into the place's gallery — it was misclassified as this-visit-only. */
    suspend fun promoteToPlace(placeId: String, visitId: String, fileName: String) {
        val visit = repository.getVisitIncludingDeleted(visitId) ?: return
        val photo = visit.photos.firstOrNull { it.fileName == fileName } ?: return
        val place = repository.getPlace(placeId) ?: return

        repository.updateVisit(visit.copy(photos = visit.photos.filterNot { it.fileName == fileName }))
        repository.savePlace(place.copy(photos = place.photos + photo.copy(kind = null, group = PhotoGroup.PLACE)))
    }

    /** Removes a photo from a visit entirely — it didn't belong to this visit's record at all. */
    suspend fun removeFromVisit(visitId: String, fileName: String) {
        val visit = repository.getVisitIncludingDeleted(visitId) ?: return
        if (visit.photos.none { it.fileName == fileName }) return
        repository.updateVisit(visit.copy(photos = visit.photos.filterNot { it.fileName == fileName }))
        imageStore.delete(fileName)
    }

    /**
     * Makes an existing gallery photo the place's cover.
     *
     * 자동 선택([fyi.moko.hotpl.domain.model.PhotoKind.pickCover])은 간판 > 음식 > 내부 >
     * 메뉴판 순으로 고르지만, 그 순서가 늘 맞지는 않는다 — 목록에서 이 가게를 알아보게 하는
     * 사진이 어느 것인지는 결국 본인이 안다. 그래서 수동 지정을 별도로 허용한다.
     *
     * 가게 갤러리에 있는 사진만 대표가 될 수 있다. [fyi.moko.hotpl.domain.model.Place.imageFileName]은
     * "[photos] 중 대표로 고른 것"이라는 뜻이고, 아직 [photos]를 모르는 구버전 클라이언트가
     * 이 필드를 읽기 때문이다 — 갤러리에 없는 파일을 넣으면 그쪽에서 없는 사진을 가리킨다.
     * 방문 사진을 대표로 삼으려면 [promoteToPlace]로 먼저 옮겨야 한다.
     */
    suspend fun setAsCover(placeId: String, fileName: String) {
        val place = repository.getPlace(placeId) ?: return
        if (place.photos.none { it.fileName == fileName }) return
        if (place.imageFileName == fileName) return
        repository.savePlace(place.copy(imageFileName = fileName))
    }

    /** Removes a photo from the place's gallery entirely. */
    suspend fun removeFromPlace(placeId: String, fileName: String) {
        val place = repository.getPlace(placeId) ?: return
        if (place.photos.none { it.fileName == fileName }) return
        repository.savePlace(place.copy(photos = place.photos.filterNot { it.fileName == fileName }))
        imageStore.delete(fileName)
    }
}
